import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useConfirmation } from "components/dialog/confirmation";
import Card from "components/card";
import { useUserInfo } from "layouts/user-info/_context/UserinfoContext";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "auth/AuthProvider";
import Switch from "components/switch";
import toast from "react-hot-toast";

const Settings = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { confirm } = useConfirmation();
  const { deactivateUser, userInfo, updateWorkspaceAdmin } = useUserInfo();
  const { user: currentUser } = useAuthContext();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Check if current user is organization owner
  // Only organization owners can set workspace admin status for users
  // This should NOT show for workspace owners or workspace admins who are not organization owners
  const isOrganizationOwner = currentUser?.current_organization?.is_owner === true;

  // Get the current workspace admin status from userInfo
  useEffect(() => {
    if (userInfo) {
      setIsAdmin(userInfo.is_admin ?? false);
    }
  }, [userInfo]);

  const handleDeactivate = async () => {
    const confirmed = await confirm({
      title: t("users.settings.deactivateAccount"),
      message: t("users.settings.confirmDeactivateAccount"),
      confirmText: t("users.settings.deactivate"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });
    if (confirmed) {
      await deactivateUser();
    }
  };

  const handleWorkspaceAdminToggle = async (checked: boolean) => {
    setIsUpdating(true);
    try {
      await updateWorkspaceAdmin(checked);
      setIsAdmin(checked);
      toast.success(
        checked
          ? t("users.settings.workspaceAdminToggle") + " enabled"
          : t("users.settings.workspaceAdminToggle") + " disabled"
      );
    } catch (err) {
      // Error is already handled in updateWorkspaceAdmin
      setIsAdmin(!checked); // Revert the toggle on error
    } finally {
      setIsUpdating(false);
    }
  };

  const handleResetPassword = async () => {
    const confirmed = await confirm({
      title: t("users.settings.resetPassword"),
      message: t("users.settings.confirmResetPassword"),
      confirmText: t("users.settings.reset"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });
    if (confirmed) {
      alert(t("users.settings.resetPasswordAlert", { id }));
    }
  };

  return (
    <div className="mt-3 flex w-full flex-col gap-5 rounded-[20px]">
      <div className="w-full max-w-xl">
        <Card extra="flex flex-col gap-4 sm:!flex-row py-6 px-4 sm:px-[30px] justify-between items-start sm:items-center w-full">
          <div className="w-full sm:w-auto">
            <h3 className="mb-px text-xl font-bold text-navy-700 dark:text-white">
              {t("users.settings.deactivateAccountTitle")}
            </h3>
            <p className="text-base text-gray-600">
              {t("users.settings.deactivateAccountDescription")}
            </p>
          </div>
          <button
            onClick={handleDeactivate}
            className="linear mt-2 flex w-full cursor-pointer items-center justify-center rounded-xl border border-red-500 px-4 py-2 text-base font-medium text-red-500 transition duration-200 hover:bg-red-50 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80 sm:mt-0 sm:w-auto"
          >
            {t("users.settings.deactivate")}
          </button>
        </Card>
      </div>
      {isOrganizationOwner && (
        <div className="w-full max-w-xl">
          <Card extra="flex flex-col gap-4 sm:!flex-row py-6 px-4 sm:px-[30px] justify-between items-start sm:items-center w-full">
            <div className="w-full sm:w-auto">
              <h3 className="mb-px text-xl font-bold text-navy-700 dark:text-white">
                {t("users.settings.workspaceAdminTitle")}
              </h3>
              <p className="text-base text-gray-600">
                {t("users.settings.workspaceAdminDescription")}
              </p>
            </div>
            <div className="mt-2 flex items-center sm:mt-0">
              <Switch
                checked={isAdmin}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleWorkspaceAdminToggle(e.target.checked)}
                disabled={isUpdating}
                color="brand"
              />
            </div>
          </Card>
        </div>
      )}
      {/* <div className="w-full max-w-xl">
        <Card extra="flex flex-col gap-4 sm:!flex-row py-6 px-4 sm:px-[30px] justify-between items-start sm:items-center w-full">
          <div className="w-full sm:w-auto">
            <h3 className="mb-px text-xl font-bold text-navy-700 dark:text-white">
              {t("users.settings.resetPasswordTitle")}
            </h3>
            <p className="text-base text-gray-600">
              {t("users.settings.resetPasswordDescription")}
            </p>
          </div>
          <button
            onClick={handleResetPassword}
            className="linear mt-2 flex w-full cursor-pointer items-center justify-center rounded-xl border border-brand-500 px-4 py-2 text-base font-medium text-brand-500 transition duration-200 hover:bg-brand-50 active:bg-brand-100 dark:active:bg-gray-50 dark:active:opacity-80 sm:mt-0 sm:w-auto"
          >
            {t("users.settings.reset")}
          </button>
        </Card>
      </div> */}
    </div>
  );
};

export default Settings;
